"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
export default function Guidance() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const instruments = [{
    label: "MARKET — ANALYSIS",
    name: "Central Ohio Market Insights",
    description: "Leverage our comprehensive data platform to track new home construction trends, pricing shifts, and neighborhood development across the central Ohio region.",
    specs: [{
      k: "Data Points",
      v: "500+"
    }, {
      k: "Accuracy",
      v: "99%"
    }, {
      k: "Updates",
      v: "Daily"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/market_analysis_data.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/market_analysis_data.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/market_analysis_data.png-wvc-sizes"
  }, {
    label: "BUILDER — COMPARISON",
    name: "Builder Performance Metrics",
    description: "Evaluate top-tier builders based on quality benchmarks, delivery timelines, and customer satisfaction ratings to ensure your investment meets your standards.",
    specs: [{
      k: "Builders",
      v: "150+"
    }, {
      k: "Rating",
      v: "4.8/5"
    }, {
      k: "Coverage",
      v: "Full"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/builder_performance_metrics.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/builder_performance_metrics.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/builder_performance_metrics.webp-wvc-sizes"
  }, {
    label: "PROJECT — TRACKING",
    name: "Construction Milestone Portal",
    description: "Stay informed with real-time updates on your home build. Our portal provides transparency into every phase, from foundation pouring to final inspection.",
    specs: [{
      k: "Phases",
      v: "12"
    }, {
      k: "Alerts",
      v: "Live"
    }, {
      k: "Access",
      v: "24/7"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/construction_milestone_portal.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/construction_milestone_portal.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/construction_milestone_portal.png-wvc-sizes"
  }, {
    label: "LEAD — GENERATION",
    name: "Qualified Buyer Connections",
    description: "Connecting serious buyers with the right builders. We streamline the discovery process, ensuring you find the perfect partner for your custom home project.",
    specs: [{
      k: "Matches",
      v: "High"
    }, {
      k: "Process",
      v: "Fast"
    }, {
      k: "Support",
      v: "Expert"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/qualified_buyer_connections.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/qualified_buyer_connections.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706309/img/qualified_buyer_connections.png-wvc-sizes"
  }];
  return <section data-section-id="3385" id="trusted-guidance" className="bg-background text-foreground relative w-full py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={header.ref} className={`mb-20 md:mb-28 max-w-4xl transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-primary mb-6">
            Trusted Guidance — The BuildIQ Advantage
          </p>
          <h2 className="font-sans font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
            Building with confidence.
          </h2>
          <p className="font-sans text-foreground mt-6 max-w-xl text-base md:text-lg leading-relaxed">
            We provide the clarity and data you need to navigate the central Ohio home-building market with absolute certainty.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {instruments.map((item, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          const offsetClass = i === 0 ? "md:mt-0" : i === 1 ? "md:mt-16" : i === 2 ? "md:-mt-8" : "md:mt-8";
          return <div key={i} data-index={i} ref={reveal.ref} className={`${offsetClass} transition-all duration-[900ms] ease-out`} style={{
            transitionDelay: reveal.isVisible ? `${i * 150}ms` : "0ms",
            opacity: reveal.isVisible ? 1 : 0,
            transform: reveal.isVisible ? "translateY(0)" : "translateY(40px)"
          }}>
                <Card className="group bg-card border-none p-6 md:p-10 lg:p-12 transition-all duration-500 ease-out hover:-translate-y-1 overflow-hidden">
                  {}
                  <div className="relative aspect-square w-full overflow-hidden bg-muted">
                    <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                    <div className="pointer-events-none absolute inset-0 bg-primary/0 transition-colors duration-500 group-hover:bg-primary/10" />
                  </div>

                  {}
                  <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-primary mt-8">
                    {item.label}
                  </p>

                  {}
                  <h3 className="font-sans font-bold text-2xl md:text-3xl lg:text-[2rem] leading-tight tracking-tight text-foreground mt-3">
                    {item.name}
                  </h3>

                  {}
                  <p className="font-sans text-foreground mt-4 text-base leading-relaxed">
                    {item.description}
                  </p>

                  {}
                  <div className="mt-8 pt-6 border-t border-border grid grid-cols-3 gap-4">
                    {item.specs.map((spec, j) => <div key={j} data-index={j} className="flex flex-col">
                        <span className="font-sans text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
                          {spec.k}
                        </span>
                        <span className="font-sans font-bold text-lg md:text-xl text-primary">
                          {spec.v}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}